/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import checked from "assets/images/checked.png";
import unchecked from "assets/images/unchecked.png";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { API_HOHOLOK, HOHOLOK_UPLOADS } from "utils";

// Data

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import axios from "axios";
import swal from "sweetalert";

function formatTanggalIndo(dateStr) {
  if (!dateStr) return "-";
  const bulan = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return `${d.getDate()} ${bulan[d.getMonth()]} ${d.getFullYear()}`;
}

function LaporanHarian() {
  const [chartData, setChartData] = useState(null);
  const [proyekList, setProyekList] = useState([]);
  const [selectedProyek, setSelectedProyek] = useState(null);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [laporanPerTanggal, setLaporanPerTanggal] = useState([]);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewList, setPreviewList] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [id_perusahaan, setIdPerusahaan] = useState(null);
  const [status, setStatus] = useState(null);
  const [loadingLaporan, setLoadingLaporan] = useState(false);
  const [statistik, setStatistik] = useState({
    totalHariKerja: 0,
    hariDenganLaporan: 0,
    hariTanpaLaporan: 0,
    persentase: 0,
  });
  const currentProyekRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    const userStorage = localStorage.getItem("user");
    if (userStorage) {
      const user = JSON.parse(localStorage.getItem("user"));
      const statusKU = user.status;
      if (statusKU == "user") {
        setIdPerusahaan(user.id_perusahaan);
      }
      setStatus(statusKU);
    }
  }, []);

  useEffect(() => {
    if (tahun && status) {
      fetchProyek();
    }
  }, [tahun, status]);

  const fetchProyek = () => {
    setLoading(true);
    const url =
      status == "user"
        ? `${API_HOHOLOK}proyek/perusahaan/${id_perusahaan}/tahun/${tahun}`
        : `${API_HOHOLOK}proyek/tahun/${tahun}`;
    axios
      .get(url)
      .then((res) => {
        const data = res.data || [];
        const dataKU = data.map((item) => ({
          label: item.nama_proyek,
          value: item.id_proyek,
          ...item,
        }));
        setProyekList(dataKU);
        setSelectedProyek(dataKU[0]);

        setLoading(false);
      })
      .catch(() => {
        setProyekList([]);
        setLoading(false);
      });
  };

  // Fetch data chart sesuai proyek & tahun
  useEffect(() => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Immediately reset state and set loading with force update
    setLaporanPerTanggal([]);
    setLoadingLaporan(true);
    setForceUpdate((prev) => prev + 1); // Force re-render

    // Update current proyek ref
    currentProyekRef.current = selectedProyek?.value;

    if (selectedProyek) {
      const proyekId = selectedProyek.value;

      axios
        .get(
          `${API_HOHOLOK}laporan-harian/proyek/${proyekId}/formatted?tahun=${tahun}`,
          { signal: abortControllerRef.current.signal }
        )
        .then((res) => {
          // Check if this response is still relevant (proyek hasn't changed)

          if (currentProyekRef.current !== proyekId) {
            return;
          }

          if (res.data && res.data.success && Array.isArray(res.data.data)) {
            const data = res.data.data;

            console.log("Data dari backend:", data);
            console.log("Jumlah data dari backend:", data.length);

            // Update statistik dari response backend
            if (res.data.info) {
              const { total_hari, hari_dengan_laporan, hari_tanpa_laporan } =
                res.data.info;
              const persentase =
                total_hari > 0
                  ? Math.round((hari_dengan_laporan / total_hari) * 100)
                  : 0;

              setStatistik({
                totalHariKerja: total_hari,
                hariDenganLaporan: hari_dengan_laporan,
                hariTanpaLaporan: hari_tanpa_laporan,
                persentase: persentase,
              });
            }

            // Data sudah diformat dari backend, langsung gunakan
            const formattedData = data.map((item) => {
              return {
                tanggal: item.tanggal,
                tanggal_formatted: item.tanggal_formatted,
                status: item.status,
                items: Array.isArray(item.laporan) ? item.laporan : [],
                total_volume: item.total_volume || 0,
                total_tenaga_kerja: item.total_tenaga_kerja || 0,
              };
            });

            setLaporanPerTanggal(formattedData);
          } else {
            setLaporanPerTanggal([]);
            setStatistik({
              totalHariKerja: 0,
              hariDenganLaporan: 0,
              hariTanpaLaporan: 0,
              persentase: 0,
            });
          }
        })
        .catch((error) => {
          // Only log error if it's not an abort error
          if (error.name !== "AbortError") {
            console.error("Error fetching laporan harian:", error);
          }

          // Check if this error is still relevant
          if (currentProyekRef.current === proyekId) {
            setLaporanPerTanggal([]);
          }
        })
        .finally(() => {
          // Only update loading if this is still the current request
          if (currentProyekRef.current === proyekId) {
            setLoadingLaporan(false);
          }
        });
    } else {
      setLoadingLaporan(false);
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedProyek, tahun]);

  // useLayoutEffect for immediate state reset before render
  useLayoutEffect(() => {
    if (selectedProyek) {
      setLaporanPerTanggal([]);
      setLoadingLaporan(true);
    }
  }, [selectedProyek?.value]);

  // Debug useEffect to track laporanPerTanggal changes

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox mb={2} display="flex" alignItems="center" gap={2}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={proyekList}
                    value={selectedProyek}
                    onChange={(event, newValue) => {
                      setSelectedProyek(newValue);
                    }}
                    loading={loading}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pilih Proyek"
                        variant="standard"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    sx={{ minWidth: 250 }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Autocomplete
                    options={[2024, 2025, 2026, 2027, 2028, 2029]}
                    value={tahun}
                    onChange={(event, newValue) => {
                      setTahun(newValue);
                    }}
                    getOptionLabel={(option) => option.toString()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tahun"
                        variant="standard"
                        fullWidth
                      />
                    )}
                    sx={{ minWidth: 120 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </MDBox>
        {selectedProyek && (
          <MDBox mt={1} p={3} borderRadius={2} bgcolor="#f0f4fa" boxShadow={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  color="info.main"
                  mb={1}
                >
                  {selectedProyek.label}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Lokasi:</b> {selectedProyek.lokasi || "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Desa:</b> {selectedProyek.desa || "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Koordinat:</b>{" "}
                  {selectedProyek.latitude && selectedProyek.longitude
                    ? `${selectedProyek.latitude}, ${selectedProyek.longitude}`
                    : "-"}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="body2" color="text">
                  <b>Tanggal Mulai:</b>{" "}
                  {formatTanggalIndo(selectedProyek.tanggal_mulai)}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Tanggal Selesai:</b>{" "}
                  {formatTanggalIndo(selectedProyek.tanggal_selesai)}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Konsultan Perencana:</b>{" "}
                  {selectedProyek.nama_konsultan_perencana ||
                    selectedProyek.konsultan_perencana?.nama ||
                    "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Kontraktor Pelaksana:</b>{" "}
                  {selectedProyek.nama_kontraktor_pelaksana ||
                    selectedProyek.kontraktor_pelaksana?.nama ||
                    "-"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  <b>Konsultan Pengawas:</b>{" "}
                  {selectedProyek.nama_konsultan_pengawas ||
                    selectedProyek.konsultan_pengawas?.nama ||
                    "-"}
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>
        )}
        {/* Statistik Laporan Harian */}
        {selectedProyek && laporanPerTanggal.length > 0 && (
          <MDBox mt={2} p={3} borderRadius={2} bgcolor="#f8f9fa" boxShadow={1}>
            <MDTypography
              variant="h6"
              fontWeight="bold"
              color="info.main"
              mb={2}
            >
              Statistik Laporan Harian
            </MDTypography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <MDBox textAlign="center">
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="info.main"
                  >
                    {statistik.totalHariKerja}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Total Hari Kerja
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={3}>
                <MDBox textAlign="center">
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {statistik.hariDenganLaporan}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Hari Dengan Laporan
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={3}>
                <MDBox textAlign="center">
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="warning.main"
                  >
                    {statistik.hariTanpaLaporan}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Hari Tanpa Laporan
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={3}>
                <MDBox textAlign="center">
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {(() => {
                      const totalLaporan = laporanPerTanggal.reduce(
                        (sum, item) => sum + item.items.length,
                        0
                      );
                      const totalTerverifikasi = laporanPerTanggal.reduce(
                        (sum, item) =>
                          sum +
                          item.items.filter((lap) => lap.verifikasi === "1")
                            .length,
                        0
                      );
                      return totalLaporan > 0
                        ? Math.round((totalTerverifikasi / totalLaporan) * 100)
                        : 0;
                    })()}
                    %
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Persentase Verifikasi
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
            <MDBox mt={3}>
              <MDTypography variant="body2" color="text.secondary" mb={1}>
                Kelengkapan Laporan: {statistik.persentase}%
              </MDTypography>
              <MDProgress
                value={statistik.persentase}
                color={
                  statistik.persentase >= 80
                    ? "success"
                    : statistik.persentase >= 60
                    ? "warning"
                    : "error"
                }
                variant="gradient"
                label={false}
              />
            </MDBox>
            <MDBox mt={2}>
              <MDTypography variant="body2" color="text.secondary" mb={1}>
                Progress Verifikasi:{" "}
                {(() => {
                  const totalLaporan = laporanPerTanggal.reduce(
                    (sum, item) => sum + item.items.length,
                    0
                  );
                  const totalTerverifikasi = laporanPerTanggal.reduce(
                    (sum, item) =>
                      sum +
                      item.items.filter((lap) => lap.verifikasi === "1").length,
                    0
                  );
                  return totalLaporan > 0
                    ? Math.round((totalTerverifikasi / totalLaporan) * 100)
                    : 0;
                })()}
                %
              </MDTypography>
              <MDProgress
                value={(() => {
                  const totalLaporan = laporanPerTanggal.reduce(
                    (sum, item) => sum + item.items.length,
                    0
                  );
                  const totalTerverifikasi = laporanPerTanggal.reduce(
                    (sum, item) =>
                      sum +
                      item.items.filter((lap) => lap.verifikasi === "1").length,
                    0
                  );
                  return totalLaporan > 0
                    ? Math.round((totalTerverifikasi / totalLaporan) * 100)
                    : 0;
                })()}
                color={(() => {
                  const totalLaporan = laporanPerTanggal.reduce(
                    (sum, item) => sum + item.items.length,
                    0
                  );
                  const totalTerverifikasi = laporanPerTanggal.reduce(
                    (sum, item) =>
                      sum +
                      item.items.filter((lap) => lap.verifikasi === "1").length,
                    0
                  );
                  const persentase =
                    totalLaporan > 0
                      ? Math.round((totalTerverifikasi / totalLaporan) * 100)
                      : 0;
                  return persentase >= 80
                    ? "success"
                    : persentase >= 60
                    ? "warning"
                    : "error";
                })()}
                variant="gradient"
                label={false}
              />
            </MDBox>
          </MDBox>
        )}
        <MDBox mt={2} mb={4}>
          {loadingLaporan ? (
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress color="info" />
              <MDTypography variant="body1" ml={2} color="text.secondary">
                Memuat data laporan harian...
              </MDTypography>
            </MDBox>
          ) : laporanPerTanggal.length === 0 ? (
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              py={6}
              textAlign="center"
            >
              <AssignmentIcon
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <MDTypography variant="h6" color="text.secondary" mb={1}>
                Tidak ada laporan harian
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                Belum ada laporan harian untuk proyek ini pada tahun {tahun}
              </MDTypography>
            </MDBox>
          ) : (
            <Grid
              container
              spacing={3}
              key={`laporan-${selectedProyek?.value}-${tahun}-${laporanPerTanggal.length}`}
            >
              {laporanPerTanggal.map(
                ({
                  tanggal,
                  tanggal_formatted,
                  status,
                  items,
                  total_volume,
                  total_tenaga_kerja,
                }) => {
                  // Tentukan apakah ada laporan berdasarkan status dan jumlah items
                  const hasLaporan =
                    status === "Ada laporan" &&
                    Array.isArray(items) &&
                    items.length > 0;

                  return (
                    <Grid item xs={12} key={tanggal}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          boxShadow: 2,
                          background: hasLaporan
                            ? "linear-gradient(90deg, #e3f2fd 0%, #f8fafc 100%)"
                            : "linear-gradient(90deg, #fff3e0 0%, #fafafa 100%)",
                          borderLeft: hasLaporan
                            ? "6px solid #1976d2"
                            : "6px solid #ff9800",
                          mb: 2,
                        }}
                      >
                        <CardContent>
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                          >
                            <MDTypography
                              variant="h6"
                              color={hasLaporan ? "info.main" : "warning.main"}
                              fontWeight="bold"
                            >
                              {tanggal_formatted}
                            </MDTypography>
                            <MDBox
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                bgcolor: hasLaporan ? "#e8f5e9" : "#fff3e0",
                                color: hasLaporan ? "#388e3c" : "#f57c00",
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                fontSize: 14,
                                fontWeight: 500,
                              }}
                            >
                              {hasLaporan ? (
                                <>
                                  <CheckCircleIcon
                                    sx={{ fontSize: 18, mr: 0.5 }}
                                  />
                                  Ada Laporan ({items.length})
                                </>
                              ) : (
                                <>
                                  <CancelIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                  Tidak Ada Laporan
                                </>
                              )}
                            </MDBox>
                          </MDBox>

                          {hasLaporan ? (
                            <>
                              <MDBox
                                mb={2}
                                p={2}
                                bgcolor="#f5f5f5"
                                borderRadius={2}
                              >
                                <Grid container spacing={2}>
                                  <Grid item xs={6} md={3}>
                                    <MDTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      <b>Total Volume:</b> {total_volume}
                                    </MDTypography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <MDTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      <b>Total Tenaga Kerja:</b>{" "}
                                      {total_tenaga_kerja}
                                    </MDTypography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <MDTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      <b>Jumlah Laporan:</b> {items.length}
                                    </MDTypography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <MDTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      <b>Pengawas Aktif:</b>{" "}
                                      {(() => {
                                        const pengawasList = items
                                          .filter(
                                            (item) =>
                                              item.nama_pengawas &&
                                              item.verifikasi === "1"
                                          )
                                          .map((item) => item.nama_pengawas);
                                        const uniquePengawas = [
                                          ...new Set(pengawasList),
                                        ];
                                        return uniquePengawas.length > 0
                                          ? uniquePengawas.join(", ")
                                          : "Belum ada";
                                      })()}
                                    </MDTypography>
                                  </Grid>
                                  <Grid item xs={6} md={3}>
                                    <MDTypography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      <b>Status Verifikasi:</b>{" "}
                                      {(() => {
                                        const terverifikasi = items.filter(
                                          (item) => item.verifikasi === "1"
                                        ).length;
                                        const belumDiverifikasi = items.filter(
                                          (item) => item.verifikasi !== "1"
                                        ).length;
                                        const totalItems = items.length;
                                        const persentaseVerifikasi =
                                          totalItems > 0
                                            ? Math.round(
                                                (terverifikasi / totalItems) *
                                                  100
                                              )
                                            : 0;

                                        return (
                                          <span>
                                            <span style={{ color: "#388e3c" }}>
                                              {terverifikasi} Terverifikasi
                                            </span>
                                            {belumDiverifikasi > 0 && (
                                              <span
                                                style={{ color: "#f57c00" }}
                                              >
                                                {" "}
                                                / {belumDiverifikasi} Belum
                                              </span>
                                            )}
                                            <br />
                                            <span
                                              style={{
                                                color:
                                                  persentaseVerifikasi >= 80
                                                    ? "#388e3c"
                                                    : persentaseVerifikasi >= 60
                                                    ? "#f57c00"
                                                    : "#f44336",
                                                fontWeight: "bold",
                                                fontSize: "0.9em",
                                              }}
                                            >
                                              ({persentaseVerifikasi}%
                                              Terverifikasi)
                                            </span>
                                          </span>
                                        );
                                      })()}
                                    </MDTypography>
                                  </Grid>
                                </Grid>
                              </MDBox>
                              <Grid container spacing={2}>
                                {items.map((lap, idx) => {
                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      md={6}
                                      lg={4}
                                      key={lap.id_laporan || idx}
                                    >
                                      <Card
                                        variant="outlined"
                                        sx={{
                                          mb: 2,
                                          borderRadius: 2,
                                          boxShadow: 1,
                                          transition:
                                            "transform 0.15s, box-shadow 0.15s",
                                          "&:hover": {
                                            transform:
                                              "translateY(-4px) scale(1.03)",
                                            boxShadow: 6,
                                          },
                                          border: "1.5px solid #e3e3e3",
                                          background: "#fff",
                                          p: 1.5,
                                        }}
                                      >
                                        <CardContent>
                                          <MDBox
                                            display="flex"
                                            alignItems="center"
                                            mb={1}
                                            gap={1}
                                          >
                                            <AssignmentIcon color="info" />
                                            <MDTypography
                                              variant="subtitle2"
                                              color="text.primary"
                                              fontWeight="bold"
                                              sx={{ flex: 1 }}
                                            >
                                              {lap.nama_pekerjaan} -{" "}
                                              {lap.uraian_pekerjaan}
                                            </MDTypography>
                                            {lap.verifikasi === "1" ? (
                                              <MDBox
                                                component="span"
                                                sx={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  bgcolor: "#e8f5e9",
                                                  color: "#388e3c",
                                                  px: 1.5,
                                                  py: 0.5,
                                                  borderRadius: 2,
                                                  fontSize: 13,
                                                  fontWeight: 500,
                                                  ml: 1,
                                                }}
                                              >
                                                <CheckCircleIcon
                                                  sx={{ fontSize: 18, mr: 0.5 }}
                                                />{" "}
                                                Terverifikasi
                                              </MDBox>
                                            ) : (
                                              <MDBox
                                                component="span"
                                                sx={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  bgcolor: "#fff3e0",
                                                  color: "#f57c00",
                                                  px: 1.5,
                                                  py: 0.5,
                                                  borderRadius: 2,
                                                  fontSize: 13,
                                                  fontWeight: 500,
                                                  ml: 1,
                                                }}
                                              >
                                                <CancelIcon
                                                  sx={{ fontSize: 18, mr: 0.5 }}
                                                />{" "}
                                                Belum Diverifikasi
                                              </MDBox>
                                            )}
                                          </MDBox>
                                          <MDTypography
                                            variant="body2"
                                            color="text.secondary"
                                            mb={1.5}
                                          >
                                            <b>Progres:</b>{" "}
                                            {(lap.volume ||
                                              lap.progres_realisasi) +
                                              " " +
                                              lap.satuan}
                                            <br />
                                            <b>Tenaga Kerja:</b>{" "}
                                            {lap.jumlah_tenaga_kerja}
                                            <br />
                                            <b>User Input:</b> {lap.nama_user}
                                            <br />
                                            <b>Pengawas:</b>{" "}
                                            {lap.nama_pengawas || "-"}
                                            <br />
                                            {lap.verifikasi === "1" &&
                                              lap.nama_pengawas && (
                                                <>
                                                  <b>Diverifikasi Oleh:</b>{" "}
                                                  {lap.nama_pengawas}
                                                  <br />
                                                </>
                                              )}
                                            <b>Status Verifikasi:</b>{" "}
                                            {lap.verifikasi === "1" ? (
                                              <span
                                                style={{
                                                  color: "#388e3c",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                ✓ Terverifikasi
                                              </span>
                                            ) : (
                                              <span
                                                style={{
                                                  color: "#f57c00",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                ⚠ Belum Diverifikasi
                                              </span>
                                            )}
                                            <br />
                                            <b>Catatan:</b> {lap.catatan || "-"}
                                          </MDTypography>
                                          {/* Foto dokumentasi */}
                                          {(() => {
                                            let fotoArr = [];
                                            try {
                                              fotoArr = JSON.parse(
                                                lap.dokumentasi_foto
                                              );
                                              if (!Array.isArray(fotoArr))
                                                fotoArr = [];
                                            } catch {
                                              fotoArr = [];
                                            }
                                            // Tampilkan maksimal 2
                                            const limited = fotoArr.slice(0, 2);
                                            return (
                                              limited.length > 0 && (
                                                <MDBox
                                                  mt={1}
                                                  display="flex"
                                                  flexWrap="wrap"
                                                  gap={1}
                                                >
                                                  {limited.map(
                                                    (foto, fotoIdx) => (
                                                      <MDBox
                                                        key={fotoIdx}
                                                        component="img"
                                                        src={`${HOHOLOK_UPLOADS}${foto}`}
                                                        alt={`Dokumentasi ${
                                                          fotoIdx + 1
                                                        }`}
                                                        sx={{
                                                          width: 60,
                                                          height: 60,
                                                          objectFit: "cover",
                                                          borderRadius: 1,
                                                          border:
                                                            "1px solid #ddd",
                                                        }}
                                                      />
                                                    )
                                                  )}
                                                </MDBox>
                                              )
                                            );
                                          })()}
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </>
                          ) : (
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              justifyContent="center"
                              alignItems="center"
                              py={4}
                              textAlign="center"
                            >
                              <CancelIcon
                                sx={{
                                  fontSize: 48,
                                  color: "warning.main",
                                  mb: 2,
                                }}
                              />
                              <MDTypography
                                variant="h6"
                                color="warning.main"
                                mb={1}
                              >
                                Tidak Ada Laporan
                              </MDTypography>
                              <MDTypography
                                variant="body2"
                                color="text.secondary"
                              >
                                Belum ada laporan harian yang diinput untuk hari
                                ini
                              </MDTypography>
                            </MDBox>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                }
              )}
            </Grid>
          )}
        </MDBox>
      </MDBox>
      {/* Dialog preview foto/video */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="md"
      >
        <DialogContent sx={{ position: "relative", p: 2, textAlign: "center" }}>
          <IconButton
            onClick={() => setOpenPreview(false)}
            sx={{ position: "absolute", right: 8, top: 8, zIndex: 2 }}
          >
            <CloseIcon />
          </IconButton>
          {previewList.length > 0 && (
            <>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={2}
              >
                {previewIndex > 0 && (
                  <IconButton onClick={() => setPreviewIndex(previewIndex - 1)}>
                    {"<"}
                  </IconButton>
                )}
                {(() => {
                  const media = previewList[previewIndex];
                  const isVideo = /\.(mp4|webm|ogg)$/i.test(media);
                  return isVideo ? (
                    <video
                      src={media}
                      style={{
                        maxWidth: 500,
                        maxHeight: 400,
                        borderRadius: 12,
                      }}
                      controls
                      autoPlay
                    />
                  ) : (
                    <img
                      src={media}
                      alt="preview"
                      style={{
                        maxWidth: 500,
                        maxHeight: 400,
                        borderRadius: 12,
                      }}
                    />
                  );
                })()}
                {previewIndex < previewList.length - 1 && (
                  <IconButton onClick={() => setPreviewIndex(previewIndex + 1)}>
                    {">"}
                  </IconButton>
                )}
              </MDBox>
              <MDTypography mt={2} variant="caption" color="text">
                {previewIndex + 1} / {previewList.length}
              </MDTypography>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default LaporanHarian;
